import { hasAccess } from "@woven/contracts";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../auth/context";
import { authErrorMessage } from "../auth/errors";
import { GithubMark, GoogleMark } from "./icons";
import { Modal } from "./ui/Modal";

/**
 * Modal sign-in dialog offering Google, GitHub, and email/password. Popups the
 * user closes themselves are treated as a cancel, not an error.
 */
export function SignInModal({ onClose }: { onClose: () => void }) {
  const {
    user,
    loading,
    access,
    accessRequested,
    error,
    signInWithGoogle,
    signInWithGithub,
    signInWithEmail,
  } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const busy = useRef(false);
  const active = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    if (user && !loading) {
      onClose();
      navigate(
        !error && accessRequested !== null && hasAccess(access, "lite") ? "/console" : "/sign-up"
      );
    }
  }, [user, loading, access, accessRequested, error, onClose, navigate]);

  async function attempt(action: () => Promise<unknown>, fallback: string) {
    if (busy.current || loading) return;
    busy.current = true;
    setSubmitting(true);
    setLocalError(null);
    try {
      await action();
    } catch (cause) {
      if (!active.current) return;
      const code = cause instanceof Error ? (cause as Error & { code?: string }).code : undefined;
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        setLocalError(null);
      } else {
        setLocalError(authErrorMessage(cause, fallback));
      }
    } finally {
      busy.current = false;
      if (active.current) setSubmitting(false);
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy.current || loading || !email.trim() || !password) {
      return;
    }
    void attempt(() => signInWithEmail(email.trim(), password), "Email sign-in failed.");
  }

  return (
    <Modal title="Sign in to Woven" onClose={onClose} busy={submitting || loading}>
      <div className="auth-methods">
        <button
          type="button"
          className="btn btn--ghost btn--block auth-provider"
          onClick={() => void attempt(signInWithGoogle, "Google sign-in failed.")}
          disabled={submitting || loading}
        >
          <GoogleMark size={18} />
          Continue with Google
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--block auth-provider"
          onClick={() => void attempt(signInWithGithub, "GitHub sign-in failed.")}
          disabled={submitting || loading}
        >
          <GithubMark size={18} />
          Continue with GitHub
        </button>

        <div className="auth-divider" role="separator">
          or continue with email
        </div>

        <form className="form auth-email-form" onSubmit={handleSubmit}>
          <div className="field">
            <label className="field-label" htmlFor="auth-email">
              Email
            </label>
            <input
              id="auth-email"
              className="input"
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={submitting || loading}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="auth-password">
              Password
            </label>
            <input
              id="auth-password"
              className="input"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              minLength={6}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={submitting || loading}
            />
          </div>
          <button
            type="submit"
            className="btn btn--primary btn--block"
            disabled={submitting || loading}
          >
            Sign in with email
          </button>
        </form>

        <p className="form-note auth-toggle">
          No account yet?{" "}
          <button
            type="button"
            className="link-button"
            onClick={() => {
              onClose();
              navigate("/sign-up");
            }}
            disabled={submitting || loading}
          >
            Create one with your email
          </button>
        </p>

        {localError ? (
          <p className="form-error" role="alert">
            {localError}
          </p>
        ) : null}

        <p className="form-note auth-note">
          Anonymous auth is not enabled — use Google, GitHub, or an email account.
        </p>
      </div>
    </Modal>
  );
}
